import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Vote,
  FileText,
  Users,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  AlertCircle,
  Loader2,
  Plus,
  ArrowLeft,
  Calendar,
  Upload,
  MessageSquare,
  History,
  Shield,
  Gavel,
  UserCheck,
  Building2,
  ChevronRight,
  Eye,
  ThumbsUp,
  ThumbsDown,
  HelpCircle,
  Minus,
  Zap,
  Send,
  RefreshCw,
  Search,
  Filter,
  Download,
  Paperclip,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  voting: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  action_required: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  expired: 'bg-slate-100 text-slate-600',
  draft: 'bg-slate-100 text-slate-600',
};

const STATUS_ICONS = {
  voting: Clock,
  approved: CheckCircle2,
  action_required: AlertCircle,
  completed: CheckCircle2,
  rejected: XCircle,
  expired: Clock,
};

const VOTE_COLORS = {
  approve: 'bg-emerald-500 hover:bg-emerald-600',
  reject: 'bg-red-500 hover:bg-red-600',
  request_info: 'bg-amber-500 hover:bg-amber-600',
  abstain: 'bg-slate-400 hover:bg-slate-500',
};

const CATEGORIES = [
  { code: 'maintenance_approval', name: 'Maintenance Approval', icon: '🔧' },
  { code: 'contractor_approval', name: 'Contractor Approval', icon: '👷' },
  { code: 'budget_approval', name: 'Budget Approval', icon: '💰' },
  { code: 'special_levy', name: 'Special Levy Decision', icon: '💳' },
  { code: 'resident_communication', name: 'Resident Communication', icon: '📢' },
  { code: 'rule_change', name: 'Rule/Policy Change', icon: '⚖️' },
  { code: 'agm_matter', name: 'AGM Matter', icon: '👥' },
  { code: 'other_governance', name: 'Other Decision', icon: '📋' },
];

const APPROVAL_RULES = [
  { code: 'majority', name: 'Majority (50% + 1)', description: 'More than half must approve' },
  { code: 'unanimous', name: 'Unanimous (100%)', description: 'All trustees must approve' },
  { code: 'two_thirds', name: 'Two-Thirds (66.67%)', description: 'Two-thirds must approve' },
  { code: 'simple_majority', name: 'Simple Majority', description: 'More approves than rejects' },
];

export default function Governance() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [stats, setStats] = useState(null);
  
  // Data
  const [resolutions, setResolutions] = useState([]);
  const [agms, setAgms] = useState([]);
  const [elections, setElections] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [trustees, setTrustees] = useState([]);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showVoteDialog, setShowVoteDialog] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [showAGMDialog, setShowAGMDialog] = useState(false);
  const [showElectionDialog, setShowElectionDialog] = useState(false);
  
  // Selected items
  const [selectedResolution, setSelectedResolution] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [resolutionForm, setResolutionForm] = useState({
    title: '',
    description: '',
    category: 'other_governance',
    approval_rule: 'majority',
    voting_deadline: '',
    is_emergency: false,
    emergency_hours: 1,
    estimated_cost: '',
    trustees_to_vote: [],
  });
  
  const [voteForm, setVoteForm] = useState({
    vote: '',
    comment: '',
  });
  
  const [actionForm, setActionForm] = useState({
    action_type: '',
    notes: '',
  });
  
  const [agmForm, setAgmForm] = useState({
    title: '',
    description: '',
    scheduled_date: '',
    venue: '',
    is_virtual: false,
    virtual_link: '',
  });
  
  const [electionForm, setElectionForm] = useState({
    title: '',
    description: '',
    positions_available: 1,
    nomination_deadline: '',
    voting_deadline: '',
  });
  
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const hoaId = user?.hoa_id || 'demo_hoa';
  
  const isTrustee = user?.role === 'trustee' || user?.is_trustee === true || 
                   user?.role === 'property_manager' || user?.role === 'platform_owner';
  const isAgent = user?.role === 'managing_agent' || user?.role === 'agent_admin' || 
                  user?.role === 'property_manager' || user?.role === 'platform_owner';

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/governance/dashboard/stats`, {
        credentials: 'include'
      });
      if (statsRes.ok) setStats(await statsRes.json());
      
      // Fetch resolutions
      const resRes = await fetch(`${API_URL}/api/governance/resolutions/list`, {
        credentials: 'include'
      });
      if (resRes.ok) setResolutions(await resRes.json());
      
      // Fetch AGMs
      const agmRes = await fetch(`${API_URL}/api/governance/agm/list`, {
        credentials: 'include'
      });
      if (agmRes.ok) setAgms(await agmRes.json());
      
      // Fetch elections
      const elecRes = await fetch(`${API_URL}/api/governance/elections/list`, {
        credentials: 'include'
      });
      if (elecRes.ok) setElections(await elecRes.json());
      
      // Fetch audit log
      const auditRes = await fetch(`${API_URL}/api/governance/audit/list?limit=50`, {
        credentials: 'include'
      });
      if (auditRes.ok) setAuditLog(await auditRes.json());
      
      // Fetch trustees for HOA
      const usersRes = await fetch(`${API_URL}/api/hoa/${hoaId}/users`, {
        credentials: 'include'
      });
      if (usersRes.ok) {
        const users = await usersRes.json();
        setTrustees(users.filter(u => u.role === 'trustee' || u.is_trustee));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/governance/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setUploadedFiles([...uploadedFiles, { url: data.url, name: file.name }]);
        toast.success('File uploaded');
      }
    } catch (error) {
      toast.error('Upload failed');
    }
  };

  const handleCreateResolution = async () => {
    if (!resolutionForm.title || !resolutionForm.description) {
      toast.error('Title and description are required');
      return;
    }
    
    if (!resolutionForm.is_emergency && !resolutionForm.voting_deadline) {
      toast.error('Voting deadline is required');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        ...resolutionForm,
        estimated_cost: resolutionForm.estimated_cost ? parseFloat(resolutionForm.estimated_cost) : null,
        supporting_documents: uploadedFiles,
        trustees_to_vote: resolutionForm.trustees_to_vote.length > 0 
          ? resolutionForm.trustees_to_vote 
          : trustees.map(t => t.user_id),
      };
      
      const res = await fetch(`${API_URL}/api/governance/resolutions/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success('Resolution created! Trustees have been notified.');
        setShowCreateDialog(false);
        setResolutionForm({
          title: '',
          description: '',
          category: 'other_governance',
          approval_rule: 'majority',
          voting_deadline: '',
          is_emergency: false,
          emergency_hours: 1,
          estimated_cost: '',
          trustees_to_vote: [],
        });
        setUploadedFiles([]);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create resolution');
      }
    } catch (error) {
      toast.error('Failed to create resolution');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewResolution = async (resolution) => {
    try {
      const res = await fetch(`${API_URL}/api/governance/resolutions/${resolution.resolution_id}`, {
        credentials: 'include'
      });
      if (res.ok) {
        setSelectedResolution(await res.json());
        setShowDetailDialog(true);
      }
    } catch (error) {
      toast.error('Failed to load resolution');
    }
  };

  const handleVote = async () => {
    if (!voteForm.vote) {
      toast.error('Please select a vote option');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/governance/resolutions/${selectedResolution.resolution_id}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(voteForm)
      });
      
      if (res.ok) {
        toast.success('Vote recorded!');
        setShowVoteDialog(false);
        setVoteForm({ vote: '', comment: '' });
        handleViewResolution(selectedResolution);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to vote');
      }
    } catch (error) {
      toast.error('Failed to submit vote');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAction = async () => {
    if (!actionForm.action_type) {
      toast.error('Please select an action type');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/governance/resolutions/${selectedResolution.resolution_id}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(actionForm)
      });
      
      if (res.ok) {
        toast.success('Action recorded!');
        setShowActionDialog(false);
        setActionForm({ action_type: '', notes: '' });
        handleViewResolution(selectedResolution);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit action');
      }
    } catch (error) {
      toast.error('Failed to submit action');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateAGM = async () => {
    if (!agmForm.title || !agmForm.scheduled_date) {
      toast.error('Title and date are required');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/governance/agm/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(agmForm)
      });
      
      if (res.ok) {
        toast.success('AGM scheduled! All residents have been notified.');
        setShowAGMDialog(false);
        setAgmForm({
          title: '',
          description: '',
          scheduled_date: '',
          venue: '',
          is_virtual: false,
          virtual_link: '',
        });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create AGM');
      }
    } catch (error) {
      toast.error('Failed to create AGM');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateElection = async () => {
    if (!electionForm.title || !electionForm.nomination_deadline || !electionForm.voting_deadline) {
      toast.error('All fields are required');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/governance/elections/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(electionForm)
      });
      
      if (res.ok) {
        toast.success('Election created!');
        setShowElectionDialog(false);
        setElectionForm({
          title: '',
          description: '',
          positions_available: 1,
          nomination_deadline: '',
          voting_deadline: '',
        });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create election');
      }
    } catch (error) {
      toast.error('Failed to create election');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryName = (code) => {
    return CATEGORIES.find(c => c.code === code)?.name || code;
  };

  const getCategoryIcon = (code) => {
    return CATEGORIES.find(c => c.code === code)?.icon || '📋';
  };

  // Filter resolutions based on active tab
  const filteredResolutions = resolutions.filter(r => {
    switch (activeTab) {
      case 'active': return r.status === 'voting';
      case 'pending': return r.can_vote && r.status === 'voting';
      case 'approved': return ['approved', 'action_required'].includes(r.status);
      case 'completed': return r.status === 'completed';
      case 'rejected': return ['rejected', 'expired'].includes(r.status);
      default: return true;
    }
  });

  if (!isTrustee && !isAgent) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Access Restricted</h2>
            <p className="text-slate-500 mb-4">
              The Governance system is only available to verified trustees and managing agents.
            </p>
            <Button onClick={() => navigate(-1)}>Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8" data-testid="governance-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-2">
                <Gavel className="w-8 h-8 text-indigo-600" />
                Governance & Resolutions
              </h1>
              <p className="text-slate-500 mt-1">Digital trustee decisions, AGMs, and elections</p>
            </div>
            <div className="flex gap-2">
              {isTrustee && (
                <>
                  <Button variant="outline" onClick={() => setShowAGMDialog(true)} data-testid="create-agm-btn">
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule AGM
                  </Button>
                  <Button variant="outline" onClick={() => setShowElectionDialog(true)} data-testid="create-election-btn">
                    <UserCheck className="w-4 h-4 mr-2" />
                    New Election
                  </Button>
                  <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setShowCreateDialog(true)} data-testid="create-resolution-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Resolution
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <p className="text-sm text-amber-600 font-medium">Active</p>
                <p className="text-2xl font-bold text-amber-700">{stats.resolutions.active}</p>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4">
                <p className="text-sm text-red-600 font-medium">Pending My Vote</p>
                <p className="text-2xl font-bold text-red-700">{stats.resolutions.pending_my_vote}</p>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <p className="text-sm text-blue-600 font-medium">Action Required</p>
                <p className="text-2xl font-bold text-blue-700">{stats.resolutions.approved}</p>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4">
                <p className="text-sm text-green-600 font-medium">Completed</p>
                <p className="text-2xl font-bold text-green-700">{stats.resolutions.completed}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500 font-medium">Upcoming AGMs</p>
                <p className="text-2xl font-bold text-slate-900">{stats.agms.upcoming}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 flex-wrap">
            <TabsTrigger value="active" data-testid="tab-active">
              <Clock className="w-4 h-4 mr-2" />
              Active
            </TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-pending">
              <AlertCircle className="w-4 h-4 mr-2" />
              Pending My Vote
            </TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Approved
            </TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Completed
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              <History className="w-4 h-4 mr-2" />
              History
            </TabsTrigger>
            <TabsTrigger value="agm" data-testid="tab-agm">
              <Users className="w-4 h-4 mr-2" />
              AGMs
            </TabsTrigger>
            <TabsTrigger value="elections" data-testid="tab-elections">
              <Vote className="w-4 h-4 mr-2" />
              Elections
            </TabsTrigger>
          </TabsList>

          {/* Resolutions List */}
          <TabsContent value="active" className="space-y-4">
            {loading ? (
              <Card><CardContent className="p-8 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" /></CardContent></Card>
            ) : filteredResolutions.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Vote className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Active Resolutions</h3>
                  <p className="text-slate-500 mb-4">Create a resolution to start the governance process</p>
                  {isTrustee && (
                    <Button onClick={() => setShowCreateDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Resolution
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              filteredResolutions.map(resolution => (
                <Card 
                  key={resolution.resolution_id} 
                  className={`hover:shadow-md transition-shadow cursor-pointer ${resolution.is_emergency ? 'border-red-300 bg-red-50' : ''}`}
                  onClick={() => handleViewResolution(resolution)}
                  data-testid={`resolution-${resolution.resolution_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {resolution.is_emergency && (
                            <Badge className="bg-red-500 text-white">
                              <Zap className="w-3 h-3 mr-1" />
                              EMERGENCY
                            </Badge>
                          )}
                          <Badge className={STATUS_COLORS[resolution.status]}>
                            {resolution.status.replace('_', ' ').toUpperCase()}
                          </Badge>
                          <span className="text-slate-500 text-sm">{getCategoryIcon(resolution.category)} {getCategoryName(resolution.category)}</span>
                        </div>
                        <h3 className="font-semibold text-slate-900 mb-1">{resolution.title}</h3>
                        <p className="text-sm text-slate-500 line-clamp-2">{resolution.description}</p>
                        <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {resolution.votes?.length || 0}/{resolution.trustees_to_vote?.length || 0} voted
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatDate(resolution.voting_deadline)}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {resolution.can_vote && (
                          <Badge className="bg-red-100 text-red-700">Vote Required</Badge>
                        )}
                        {resolution.user_voted && (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Voted: {resolution.user_vote}
                          </Badge>
                        )}
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                    
                    {/* Vote Progress */}
                    <div className="mt-4">
                      <div className="flex gap-2 text-xs mb-1">
                        <span className="text-emerald-600">Approve: {resolution.vote_summary?.approve || 0}</span>
                        <span className="text-red-600">Reject: {resolution.vote_summary?.reject || 0}</span>
                        <span className="text-amber-600">Info: {resolution.vote_summary?.request_info || 0}</span>
                      </div>
                      <Progress 
                        value={((resolution.votes?.length || 0) / (resolution.trustees_to_vote?.length || 1)) * 100} 
                        className="h-2"
                      />
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="pending" className="space-y-4">
            {filteredResolutions.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">All Caught Up!</h3>
                  <p className="text-slate-500">No resolutions pending your vote</p>
                </CardContent>
              </Card>
            ) : (
              filteredResolutions.map(resolution => (
                <Card 
                  key={resolution.resolution_id} 
                  className={`hover:shadow-md transition-shadow cursor-pointer border-red-200 ${resolution.is_emergency ? 'bg-red-50' : ''}`}
                  onClick={() => handleViewResolution(resolution)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {resolution.is_emergency && <Badge className="bg-red-500 text-white"><Zap className="w-3 h-3 mr-1" />URGENT</Badge>}
                          <span className="text-sm text-slate-500">{getCategoryIcon(resolution.category)} {getCategoryName(resolution.category)}</span>
                        </div>
                        <h3 className="font-semibold text-slate-900">{resolution.title}</h3>
                        <p className="text-sm text-slate-500 mt-1">Deadline: {formatDate(resolution.voting_deadline)}</p>
                      </div>
                      <Button className="bg-indigo-600 hover:bg-indigo-700">
                        <Vote className="w-4 h-4 mr-2" />
                        Vote Now
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="approved" className="space-y-4">
            {filteredResolutions.length === 0 ? (
              <Card><CardContent className="p-12 text-center"><p className="text-slate-500">No approved resolutions awaiting action</p></CardContent></Card>
            ) : (
              filteredResolutions.map(resolution => (
                <Card key={resolution.resolution_id} className="hover:shadow-md transition-shadow cursor-pointer border-blue-200" onClick={() => handleViewResolution(resolution)}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Badge className={STATUS_COLORS[resolution.status]}>{resolution.status.replace('_', ' ')}</Badge>
                        <h3 className="font-semibold text-slate-900 mt-2">{resolution.title}</h3>
                        <p className="text-sm text-slate-500 mt-1">Approved: {formatDate(resolution.approved_at)}</p>
                      </div>
                      {isAgent && resolution.status === 'action_required' && (
                        <Button className="bg-blue-600 hover:bg-blue-700">
                          <Send className="w-4 h-4 mr-2" />
                          Take Action
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="completed" className="space-y-4">
            {filteredResolutions.length === 0 ? (
              <Card><CardContent className="p-12 text-center"><p className="text-slate-500">No completed resolutions</p></CardContent></Card>
            ) : (
              filteredResolutions.map(resolution => (
                <Card key={resolution.resolution_id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleViewResolution(resolution)}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <Badge className="bg-green-100 text-green-700">Completed</Badge>
                    </div>
                    <h3 className="font-semibold text-slate-900">{resolution.title}</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Completed by {resolution.completed_by_name} on {formatDate(resolution.completed_at)}
                    </p>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="history" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Governance Audit Trail
                </CardTitle>
              </CardHeader>
              <CardContent>
                {auditLog.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No governance actions recorded yet</p>
                ) : (
                  <div className="space-y-3">
                    {auditLog.map((entry, i) => (
                      <div key={entry.audit_id || i} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <Gavel className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">
                            {entry.actor_name} <span className="font-normal text-slate-500">{entry.action.replace(/_/g, ' ')}</span>
                          </p>
                          <p className="text-sm text-slate-500">
                            {entry.reference_type}: {entry.details?.title || entry.reference_id}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">{formatDate(entry.timestamp)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="agm" className="space-y-4">
            {agms.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No AGMs Scheduled</h3>
                  <p className="text-slate-500 mb-4">Schedule your Annual General Meeting</p>
                  {isTrustee && (
                    <Button onClick={() => setShowAGMDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Schedule AGM
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              agms.map(agm => (
                <Card key={agm.agm_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Badge className={agm.status === 'scheduled' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                          {agm.status}
                        </Badge>
                        <h3 className="font-semibold text-slate-900 mt-2">{agm.title}</h3>
                        <p className="text-sm text-slate-500 mt-1">
                          <Calendar className="w-4 h-4 inline mr-1" />
                          {formatDate(agm.scheduled_date)}
                          {agm.venue && <span className="ml-2">📍 {agm.venue}</span>}
                        </p>
                        <p className="text-sm text-slate-500 mt-1">
                          {agm.attendees?.length || 0} registered
                        </p>
                      </div>
                      <Button variant="outline">View Details</Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="elections" className="space-y-4">
            {elections.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <UserCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Elections</h3>
                  <p className="text-slate-500 mb-4">Create a trustee election</p>
                  {isTrustee && (
                    <Button onClick={() => setShowElectionDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      New Election
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              elections.map(election => (
                <Card key={election.election_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Badge className={election.status === 'voting' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}>
                          {election.status}
                        </Badge>
                        <h3 className="font-semibold text-slate-900 mt-2">{election.title}</h3>
                        <p className="text-sm text-slate-500 mt-1">
                          {election.positions_available} position(s) • {election.candidates?.length || 0} candidates
                        </p>
                        <p className="text-sm text-slate-500">
                          Voting ends: {formatDate(election.voting_deadline)}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2">
                        {election.user_voted ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Voted
                          </Badge>
                        ) : election.status === 'voting' ? (
                          <Button>Vote Now</Button>
                        ) : null}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Resolution Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gavel className="w-5 h-5 text-indigo-600" />
              Create Trustee Resolution
            </DialogTitle>
            <DialogDescription>
              Create a resolution for trustees to vote on
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Emergency Toggle */}
            <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-red-600" />
                <div>
                  <p className="font-medium text-red-900">Emergency Resolution</p>
                  <p className="text-sm text-red-600">Rapid voting for urgent decisions</p>
                </div>
              </div>
              <Switch 
                checked={resolutionForm.is_emergency}
                onCheckedChange={(checked) => setResolutionForm({...resolutionForm, is_emergency: checked})}
              />
            </div>
            
            {resolutionForm.is_emergency && (
              <div className="space-y-2">
                <Label>Emergency Voting Window</Label>
                <Select value={String(resolutionForm.emergency_hours)} onValueChange={(v) => setResolutionForm({...resolutionForm, emergency_hours: parseFloat(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.5">30 minutes</SelectItem>
                    <SelectItem value="1">1 hour</SelectItem>
                    <SelectItem value="3">3 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Resolution Title *</Label>
              <Input 
                value={resolutionForm.title}
                onChange={(e) => setResolutionForm({...resolutionForm, title: e.target.value})}
                placeholder="e.g., Approval for roof repair contractor"
                data-testid="resolution-title"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea 
                value={resolutionForm.description}
                onChange={(e) => setResolutionForm({...resolutionForm, description: e.target.value})}
                placeholder="Provide detailed information about the resolution..."
                rows={4}
                data-testid="resolution-description"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={resolutionForm.category} onValueChange={(v) => setResolutionForm({...resolutionForm, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.code} value={cat.code}>
                        {cat.icon} {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Approval Rule</Label>
                <Select value={resolutionForm.approval_rule} onValueChange={(v) => setResolutionForm({...resolutionForm, approval_rule: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {APPROVAL_RULES.map(rule => (
                      <SelectItem key={rule.code} value={rule.code}>
                        {rule.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {!resolutionForm.is_emergency && (
              <div className="space-y-2">
                <Label>Voting Deadline *</Label>
                <Input 
                  type="datetime-local"
                  value={resolutionForm.voting_deadline}
                  onChange={(e) => setResolutionForm({...resolutionForm, voting_deadline: e.target.value})}
                  data-testid="resolution-deadline"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Estimated Cost (Optional)</Label>
              <Input 
                type="number"
                value={resolutionForm.estimated_cost}
                onChange={(e) => setResolutionForm({...resolutionForm, estimated_cost: e.target.value})}
                placeholder="0.00"
              />
            </div>
            
            {/* File Upload */}
            <div className="space-y-2">
              <Label>Supporting Documents</Label>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full">
                <Upload className="w-4 h-4 mr-2" />
                Upload Document
              </Button>
              {uploadedFiles.length > 0 && (
                <div className="space-y-2 mt-2">
                  {uploadedFiles.map((file, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm bg-slate-100 p-2 rounded">
                      <Paperclip className="w-4 h-4" />
                      {file.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateResolution} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Create Resolution
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolution Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedResolution?.is_emergency && <Zap className="w-5 h-5 text-red-500" />}
              Resolution Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedResolution && (
            <div className="space-y-6">
              {/* Status & Category */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={STATUS_COLORS[selectedResolution.status]}>
                  {selectedResolution.status.replace('_', ' ').toUpperCase()}
                </Badge>
                <Badge variant="outline">
                  {getCategoryIcon(selectedResolution.category)} {getCategoryName(selectedResolution.category)}
                </Badge>
                {selectedResolution.is_emergency && (
                  <Badge className="bg-red-500 text-white">EMERGENCY</Badge>
                )}
              </div>
              
              {/* Title & Description */}
              <div>
                <h3 className="text-xl font-semibold text-slate-900">{selectedResolution.title}</h3>
                <p className="text-slate-600 mt-2 whitespace-pre-wrap">{selectedResolution.description}</p>
              </div>
              
              {/* Meta Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Created By</p>
                  <p className="font-medium">{selectedResolution.created_by_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Created</p>
                  <p className="font-medium">{formatDate(selectedResolution.created_at)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Voting Deadline</p>
                  <p className="font-medium">{formatDate(selectedResolution.voting_deadline)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Approval Rule</p>
                  <p className="font-medium">{APPROVAL_RULES.find(r => r.code === selectedResolution.approval_rule)?.name}</p>
                </div>
                {selectedResolution.estimated_cost && (
                  <div>
                    <p className="text-slate-500">Estimated Cost</p>
                    <p className="font-medium">R{selectedResolution.estimated_cost.toLocaleString()}</p>
                  </div>
                )}
              </div>
              
              {/* Vote Progress */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium mb-2">Voting Progress</p>
                <div className="flex gap-4 text-sm mb-2">
                  <span className="flex items-center gap-1 text-emerald-600">
                    <ThumbsUp className="w-4 h-4" /> Approve: {selectedResolution.vote_summary?.approve || 0}
                  </span>
                  <span className="flex items-center gap-1 text-red-600">
                    <ThumbsDown className="w-4 h-4" /> Reject: {selectedResolution.vote_summary?.reject || 0}
                  </span>
                  <span className="flex items-center gap-1 text-amber-600">
                    <HelpCircle className="w-4 h-4" /> Info: {selectedResolution.vote_summary?.request_info || 0}
                  </span>
                </div>
                <Progress 
                  value={(selectedResolution.votes_received / selectedResolution.total_trustees) * 100} 
                  className="h-3"
                />
                <p className="text-sm text-slate-500 mt-1">
                  {selectedResolution.votes_received} of {selectedResolution.total_trustees} trustees voted
                </p>
              </div>
              
              {/* Individual Votes */}
              {selectedResolution.votes?.length > 0 && (
                <div>
                  <p className="font-medium mb-2">Votes Cast</p>
                  <div className="space-y-2">
                    {selectedResolution.votes.map((vote, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{vote.name}</p>
                          {vote.comment && <p className="text-sm text-slate-500">{vote.comment}</p>}
                        </div>
                        <Badge className={VOTE_COLORS[vote.vote]?.replace('hover:', '')}>{vote.vote}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Supporting Documents */}
              {selectedResolution.supporting_documents?.length > 0 && (
                <div>
                  <p className="font-medium mb-2">Supporting Documents</p>
                  <div className="space-y-2">
                    {selectedResolution.supporting_documents.map((doc, i) => (
                      <a key={i} href={`${API_URL}${doc.url}`} target="_blank" rel="noopener noreferrer"
                         className="flex items-center gap-2 p-2 bg-slate-100 rounded hover:bg-slate-200">
                        <FileText className="w-4 h-4" />
                        {doc.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Action Notes */}
              {selectedResolution.action_notes?.length > 0 && (
                <div>
                  <p className="font-medium mb-2">Action Updates</p>
                  <div className="space-y-2">
                    {selectedResolution.action_notes.map((note, i) => (
                      <div key={i} className="p-3 bg-blue-50 rounded-lg">
                        <p className="font-medium text-blue-900">{note.action_type.replace('_', ' ')}</p>
                        <p className="text-sm text-blue-700">{note.notes}</p>
                        <p className="text-xs text-blue-500 mt-1">By {note.submitted_by_name} • {formatDate(note.timestamp)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-wrap gap-2">
            {selectedResolution?.can_vote && (
              <Button onClick={() => { setShowDetailDialog(false); setShowVoteDialog(true); }} className="bg-indigo-600 hover:bg-indigo-700">
                <Vote className="w-4 h-4 mr-2" />
                Cast Vote
              </Button>
            )}
            {isAgent && selectedResolution?.status === 'action_required' && (
              <Button onClick={() => { setShowDetailDialog(false); setShowActionDialog(true); }} className="bg-blue-600 hover:bg-blue-700">
                <Send className="w-4 h-4 mr-2" />
                Submit Action
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vote Dialog */}
      <Dialog open={showVoteDialog} onOpenChange={setShowVoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cast Your Vote</DialogTitle>
            <DialogDescription>
              {selectedResolution?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant={voteForm.vote === 'approve' ? 'default' : 'outline'}
                className={voteForm.vote === 'approve' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
                onClick={() => setVoteForm({...voteForm, vote: 'approve'})}
              >
                <ThumbsUp className="w-4 h-4 mr-2" />
                Approve
              </Button>
              <Button 
                variant={voteForm.vote === 'reject' ? 'default' : 'outline'}
                className={voteForm.vote === 'reject' ? 'bg-red-500 hover:bg-red-600' : ''}
                onClick={() => setVoteForm({...voteForm, vote: 'reject'})}
              >
                <ThumbsDown className="w-4 h-4 mr-2" />
                Reject
              </Button>
              <Button 
                variant={voteForm.vote === 'request_info' ? 'default' : 'outline'}
                className={voteForm.vote === 'request_info' ? 'bg-amber-500 hover:bg-amber-600' : ''}
                onClick={() => setVoteForm({...voteForm, vote: 'request_info'})}
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Request Info
              </Button>
              <Button 
                variant={voteForm.vote === 'abstain' ? 'default' : 'outline'}
                className={voteForm.vote === 'abstain' ? 'bg-slate-500 hover:bg-slate-600' : ''}
                onClick={() => setVoteForm({...voteForm, vote: 'abstain'})}
              >
                <Minus className="w-4 h-4 mr-2" />
                Abstain
              </Button>
            </div>
            
            <div className="space-y-2">
              <Label>Comment (Optional)</Label>
              <Textarea 
                value={voteForm.comment}
                onChange={(e) => setVoteForm({...voteForm, comment: e.target.value})}
                placeholder="Add a comment to explain your vote..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVoteDialog(false)}>Cancel</Button>
            <Button onClick={handleVote} disabled={submitting || !voteForm.vote} className="bg-indigo-600 hover:bg-indigo-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Submit Vote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Dialog (Managing Agent) */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Action</DialogTitle>
            <DialogDescription>
              Record your action on this approved resolution
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Action Type</Label>
              <Select value={actionForm.action_type} onValueChange={(v) => setActionForm({...actionForm, action_type: v})}>
                <SelectTrigger><SelectValue placeholder="Select action..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="assign_contractor">Assign Contractor</SelectItem>
                  <SelectItem value="upload_invoice">Upload Invoice</SelectItem>
                  <SelectItem value="send_notice">Send Notice to Residents</SelectItem>
                  <SelectItem value="upload_report">Upload Report</SelectItem>
                  <SelectItem value="mark_complete">Mark as Complete</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={actionForm.notes}
                onChange={(e) => setActionForm({...actionForm, notes: e.target.value})}
                placeholder="Describe the action taken..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitAction} disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create AGM Dialog */}
      <Dialog open={showAGMDialog} onOpenChange={setShowAGMDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule AGM</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input 
                value={agmForm.title}
                onChange={(e) => setAgmForm({...agmForm, title: e.target.value})}
                placeholder="Annual General Meeting 2026"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={agmForm.description}
                onChange={(e) => setAgmForm({...agmForm, description: e.target.value})}
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Date & Time *</Label>
              <Input 
                type="datetime-local"
                value={agmForm.scheduled_date}
                onChange={(e) => setAgmForm({...agmForm, scheduled_date: e.target.value})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Virtual Meeting</Label>
              <Switch 
                checked={agmForm.is_virtual}
                onCheckedChange={(checked) => setAgmForm({...agmForm, is_virtual: checked})}
              />
            </div>
            
            {agmForm.is_virtual ? (
              <div className="space-y-2">
                <Label>Meeting Link</Label>
                <Input 
                  value={agmForm.virtual_link}
                  onChange={(e) => setAgmForm({...agmForm, virtual_link: e.target.value})}
                  placeholder="https://zoom.us/..."
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Venue</Label>
                <Input 
                  value={agmForm.venue}
                  onChange={(e) => setAgmForm({...agmForm, venue: e.target.value})}
                  placeholder="Building common room"
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAGMDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateAGM} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
              Schedule AGM
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Election Dialog */}
      <Dialog open={showElectionDialog} onOpenChange={setShowElectionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Trustee Election</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input 
                value={electionForm.title}
                onChange={(e) => setElectionForm({...electionForm, title: e.target.value})}
                placeholder="Board Trustee Election 2026"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={electionForm.description}
                onChange={(e) => setElectionForm({...electionForm, description: e.target.value})}
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Positions Available</Label>
              <Input 
                type="number"
                min="1"
                value={electionForm.positions_available}
                onChange={(e) => setElectionForm({...electionForm, positions_available: parseInt(e.target.value)})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Nomination Deadline *</Label>
              <Input 
                type="datetime-local"
                value={electionForm.nomination_deadline}
                onChange={(e) => setElectionForm({...electionForm, nomination_deadline: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Voting Deadline *</Label>
              <Input 
                type="datetime-local"
                value={electionForm.voting_deadline}
                onChange={(e) => setElectionForm({...electionForm, voting_deadline: e.target.value})}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowElectionDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateElection} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserCheck className="w-4 h-4 mr-2" />}
              Create Election
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
